import { useState, ReactElement } from "react";

import "./TransportationExpenseDialog.css";
import { Button, Form } from "react-bulma-components";
import styled from "styled-components";

import {
  TRANSPORTATION_TYPE,
  TRANSPORTATION_TYPE_NAME,
} from "enums/transportationType";
import { slashedYMDAndDayOfWeek, hyphenedYMDFromString } from "helpers/date";
import { RESPONSIVE } from "helpers/responsive";
import { validateNumber } from "helpers/validator";
import { NewMovementLog } from "models/movementLog";
import { NewMovementRoute } from "models/movementRoute";
import { DaySummary } from "models/travelExpense";
import * as Table from "molecules/Table";

export const EDIT_MODE = {
  PUBLIC_TRANSPORTATION: "PUBLIC_TRANSPORTATION",
  MANUAL: "MANUAL",
} as const;

const { Field, Control, Checkbox } = Form;

export const TransitsTableFormInput = styled.input`
  width: 50px;

  @media ${RESPONSIVE.DESKTOP} {
    width: 95px;
  }
`;

export const TransitsTableFormInputUnit = styled.span`
  line-height: 1.1rem;
  padding-left: 0.3rem;

  @media ${RESPONSIVE.DESKTOP} {
    line-height: 2.4rem;
    padding-left: 0.6rem;
  }
`;

const defaultMovementRouteForManual = (): NewMovementRoute => ({
  arrivalId: undefined,
  arrivalName: "",
  departureId: undefined,
  departureName: "",
  distanceKm: 0,
  fare: undefined,
  orderNo: 0,
  time: 0,
  transportationType: 1,
  useCommuterPass: false,
});

const getMaxOrderNo = (routes: NewMovementRoute[]) =>
  Math.max.apply(
    null,
    routes.map((r) => r.orderNo)
  );

interface TransportationExpenseDetailsInManualModeProps {
  routes: NewMovementRoute[];
  setRoutes: (routes: NewMovementRoute[]) => void;
  createMovementLog: (routes: NewMovementLog) => void;
}

function TransportationExpenseDetailsInManualMode({
  routes,
  setRoutes,
  createMovementLog,
}: TransportationExpenseDetailsInManualModeProps): ReactElement | null {
  const changeTransportationType = (
    index: number,
    transportationType: number
  ) => {
    // eslint-disable-next-line no-param-reassign
    routes[index].transportationType = transportationType;
    setRoutes([...routes]);
  };

  const changeArrivalName = (name: string, index: number) => {
    // eslint-disable-next-line no-param-reassign
    routes[index].arrivalName = name;
    setRoutes([...routes]);
  };

  const changeDepartureName = (name: string, index: number) => {
    // eslint-disable-next-line no-param-reassign
    routes[index].departureName = name;
    setRoutes([...routes]);
  };

  const changeFare = (index: number, fare?: number) => {
    // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-non-null-assertion
    routes[index].fare = fare!;
    setRoutes([...routes]);
  };

  const changeUseCommuterPass = (index: number, useCommuterPass: boolean) => {
    // eslint-disable-next-line no-param-reassign
    routes[index].useCommuterPass = useCommuterPass;
    // eslint-disable-next-line no-param-reassign
    routes[index].fare = 0;
    setRoutes([...routes]);
  };

  const renderTransportationType = (
    index: number,
    selectedTransportationType?: number
  ) => {
    const options = [];
    options.push(
      <option key={TRANSPORTATION_TYPE.train} value={TRANSPORTATION_TYPE.train}>
        {TRANSPORTATION_TYPE_NAME.train}
      </option>
    );
    options.push(
      <option key={TRANSPORTATION_TYPE.bus} value={TRANSPORTATION_TYPE.bus}>
        {TRANSPORTATION_TYPE_NAME.bus}
      </option>
    );
    return (
      <div className="select">
        <select
          value={selectedTransportationType}
          onChange={(event) => {
            changeTransportationType(
              index,
              parseInt(event.target.selectedOptions[0].value, 10)
            );
          }}
        >
          {options}
        </select>
      </div>
    );
  };

  const renderFareInput = (
    index: number,
    useCommuterPass: boolean,
    fare?: number
  ) => {
    if (useCommuterPass) {
      return (
        <>
          0<TransitsTableFormInputUnit>円</TransitsTableFormInputUnit>
        </>
      );
    }
    return (
      <>
        <TransitsTableFormInput
          className="input"
          type="number"
          min="0"
          value={fare || fare === 0 ? fare : ""}
          onChange={(event) => {
            // eslint-disable-next-line no-shadow
            const fare = parseInt(event.target.value, 10);
            if (fare === 0 || validateNumber(fare)) {
              changeFare(index, fare);
            } else {
              changeFare(index);
            }
          }}
        />
        <TransitsTableFormInputUnit>円</TransitsTableFormInputUnit>
      </>
    );
  };

  const addMovementRoute = (index: number) => {
    const newRoute: NewMovementRoute = defaultMovementRouteForManual();
    newRoute.orderNo = getMaxOrderNo(routes) + 1;
    routes.splice(index + 1, 0, newRoute);

    setRoutes([...routes]);
  };

  const delMovementRoute = (index: number) => {
    routes.splice(index, 1);
    setRoutes([...routes]);
  };

  const validateMovementRoute = () =>
    routes.every(
      (detail) =>
        detail.arrivalName !== "" &&
        detail.departureName !== "" &&
        detail.fare !== undefined
    );

  const reassignOrderNo = () =>
    routes.map((detail, index) => {
      // eslint-disable-next-line no-param-reassign
      detail.orderNo = index;
      return detail;
    });

  const handleSelect = () => {
    const totalFare = routes.reduce(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (acc: number, detail) => acc + detail.fare!,
      0
    );
    const movementLog = {
      at: "",
      departureId: undefined,
      departureCode: "",
      departureName: "",
      departureTime: undefined,
      arrivalId: undefined,
      arrivalCode: "",
      arrivalName: "",
      arrivalTime: undefined,
      totalFare,
      totalTime: 0,
      totalDistance: 0,
      totalDistanceKm: 0,
      account: 2,
      modifiedBy: 1,
      preparedRouteId: undefined,
      movementRoutes: reassignOrderNo(),
    } as NewMovementLog;

    createMovementLog(movementLog);
  };

  return (
    <>
      <div className="cw-transportation-expense-overview">
        <div className="cw-transportation-expense-overview-title">
          <h4 className="title is-4">交通費</h4>
        </div>
        <Button
          color="primary"
          disabled={!validateMovementRoute()}
          onClick={() => handleSelect()}
        >
          選択
        </Button>
      </div>
      <Table.Table>
        <thead>
          <tr>
            <Table.ThMedium>交通手段</Table.ThMedium>
            <Table.Th>出発地</Table.Th>
            <Table.Th>到着地</Table.Th>
            <Table.ThMedium>金額</Table.ThMedium>
            <Table.ThUseCommuterPass>定期</Table.ThUseCommuterPass>
            <Table.ThSmall />
          </tr>
        </thead>
        <tbody>
          {routes.map((route, index) => (
            <tr key={route.orderNo}>
              <Table.Td>
                {renderTransportationType(index, route.transportationType)}
              </Table.Td>
              <Table.Td>
                <input
                  className="input"
                  type="text"
                  value={route.departureName || ""}
                  onChange={(event) => {
                    changeDepartureName(event.target.value, index);
                  }}
                />
              </Table.Td>
              <Table.Td>
                <input
                  className="input"
                  type="text"
                  value={route.arrivalName || ""}
                  onChange={(event) => {
                    changeArrivalName(event.target.value, index);
                  }}
                />
              </Table.Td>
              <Table.Td>
                {renderFareInput(index, route.useCommuterPass, route.fare)}
              </Table.Td>
              <Table.TdUseCommuterPass>
                <Field>
                  <Control>
                    <Checkbox
                      checked={route.useCommuterPass}
                      onChange={(event) => {
                        changeUseCommuterPass(index, event.target.checked);
                      }}
                    />
                  </Control>
                </Field>
              </Table.TdUseCommuterPass>
              <Table.Td>
                <Button color="primary" onClick={() => addMovementRoute(index)}>
                  <i className="fa fa-xs fa-plus" />
                </Button>
                {routes.length !== 1 && (
                  <Button
                    color="danger"
                    onClick={() => delMovementRoute(index)}
                  >
                    <i className="fa fa-xs fa-times" />
                  </Button>
                )}
              </Table.Td>
            </tr>
          ))}
        </tbody>
      </Table.Table>
    </>
  );
}

interface TransportationExpenseDetailsProps {
  editMode: EditMode;
  routes: NewMovementRoute[];
  setRoutes: (routes: NewMovementRoute[]) => void;
  createMovementLog: (routes: NewMovementLog) => void;
}

function TransportationExpenseDetails({
  editMode,
  routes,
  setRoutes,
  createMovementLog,
}: TransportationExpenseDetailsProps): ReactElement | null {
  switch (editMode) {
    case EDIT_MODE.MANUAL:
      return (
        <TransportationExpenseDetailsInManualMode
          routes={routes}
          setRoutes={setRoutes}
          createMovementLog={createMovementLog}
        />
      );
    default:
      return null;
  }
}

export type EditMode = typeof EDIT_MODE[keyof typeof EDIT_MODE];

export interface TransportationExpenseDialogProps {
  toggleDialog: (daySummary: DaySummary) => void;
  registerTransportationExpense: (movementLog: NewMovementLog) => void;
  isVisible: boolean;
  transportationExpenseDaySummary: DaySummary;
}

export function TransportationExpenseDialog({
  registerTransportationExpense,
  toggleDialog,
  isVisible,
  transportationExpenseDaySummary,
}: TransportationExpenseDialogProps): ReactElement | null {
  const [routes, setRoutes] = useState([defaultMovementRouteForManual()]);

  const initState = () => {
    setRoutes([defaultMovementRouteForManual()]);
  };

  const handleClose = () => {
    toggleDialog({} as DaySummary);
    initState();
  };

  const daySummaryAt = slashedYMDAndDayOfWeek(
    new Date(transportationExpenseDaySummary.date)
  );

  const createMovementLog = (movementLog: NewMovementLog) => {
    // eslint-disable-next-line no-param-reassign
    movementLog.at = hyphenedYMDFromString(
      transportationExpenseDaySummary.date
    );
    registerTransportationExpense(movementLog);
    handleClose();
  };

  return (
    <div className={`modal ${isVisible ? "is-active" : ""}`}>
      <div className="modal-background" />
      <div className="modal-card cw-transportation-expense-dialog-modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">{`${daySummaryAt} 交通費追加`}</p>
          <button
            type="button"
            className="delete"
            aria-label="close"
            onClick={(_event) => handleClose()}
          />
        </header>
        <section className="modal-card-body">
          <TransportationExpenseDetails
            editMode={EDIT_MODE.MANUAL}
            routes={routes}
            setRoutes={setRoutes}
            createMovementLog={createMovementLog}
          />
          <div className="cw-transportation-expense-dialog-header" />
        </section>
        <footer className="modal-card-foot">
          <Button onClick={() => handleClose()}>キャンセル</Button>
        </footer>
      </div>
    </div>
  );
}
